import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Container, Grid, Paper, Divider, Button, Stack } from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PublicIcon from '@mui/icons-material/Public';
import DiamondIcon from '@mui/icons-material/Diamond';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import GavelIcon from '@mui/icons-material/Gavel';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

function About() {
  const features = [
    { 
      icon: <PublicIcon />, 
      title: 'Global Accessibility', 
      desc: 'Trade, transfer, and hold anywhere in the world with the speed and efficiency of blockchain technology.', 
      link: 'See network coverage' 
    },
    { 
      icon: <AccountBalanceIcon />, 
      title: 'DeFi Integration', 
      desc: 'Seamlessly integrate into your DeFi portfolio for lending', 
      link: 'DeFi opportunities' 
    },
    { 
      icon: <GavelIcon />, 
      title: 'Regulatory Compliant', 
      desc: 'Full compliance with international regulatory frameworks, with transparent reporting.', 
      link: 'Regulatory overview' 
    },
  ];

  return (
    <Box sx={{ backgroundColor: '#000000', minHeight: '100vh', overflowX: 'hidden' }}>
      
      {/* ============ HERO SECTION ============ */}
      <Box
        sx={{
          position: 'relative',
          height: '60vh',
          minHeight: '500px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          overflow: 'hidden',
          backgroundImage: 'url(/images/hero.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {/* Dark Overlay */}
        <Box sx={{ 
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
          background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0.85) 50%, rgba(0, 0, 0, 0.98) 100%)', 
          zIndex: 1 
        }} />
        
        {/* Slow Zoom */}
        <Box sx={{ 
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
          backgroundImage: 'url(/images/hero.png)', backgroundSize: 'cover', backgroundPosition: 'center', 
          animation: 'slowZoom 25s ease-in-out infinite alternate', 
          '@keyframes slowZoom': { '0%': { transform: 'scale(1)' }, '100%': { transform: 'scale(1.1)' } }, 
          zIndex: 0 
        }} />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 10, pt: { xs: '120px', md: '150px' } }}>
          
          <Typography variant="h1" component="h1" sx={{
            fontFamily: "'Playfair Display', serif", fontWeight: 600, fontSize: { xs: '2.8rem', sm: '3.2rem', md: '4.5rem' }, lineHeight: 1.1, mb: 4,
            background: 'linear-gradient(90deg, #ffffff 0%, #d4af37 50%, #ffffff 100%)', backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', animation: 'shimmer 6s linear infinite',
            filter: 'drop-shadow(0 4px 15px rgba(0,0,0,0.8))',
            '@keyframes shimmer': { '0%': { backgroundPosition: '200% center' }, '100%': { backgroundPosition: '-200% center' } },
          }}>
            The Diamond Dollar Standard
          </Typography>

          <Typography variant="body1" sx={{ color: '#e0e0e0', fontSize: { xs: '1rem', md: '1.2rem' }, fontWeight: 300, maxWidth: '650px', mx: 'auto', mb: 5, lineHeight: 1.8, textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>
          
            Bridging the gap between traditional wealth and the future of digital finance.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button component={RouterLink} to="/contact" variant="contained" endIcon={<ArrowForwardIcon />} sx={{
              background: 'linear-gradient(135deg, #d4af37 0%, #b8962f 100%)', 
              color: '#000000', 
              fontWeight: 800, 
              fontSize: '0.85rem', 
              letterSpacing: '2px', 
              textTransform: 'uppercase', 
              padding: '18px 40px', 
              borderRadius: '4px', 
              boxShadow: '0 10px 40px rgba(212, 175, 55, 0.4)', 
              transition: 'all 0.5s ease',
              '&:hover': { 
                background: 'linear-gradient(135deg, #e8c84a 0%, #d4af37 100%)', 
                color: '#000000', 
                boxShadow: '0 15px 50px rgba(212, 175, 55, 0.5)', 
                transform: 'translateY(-3px)' 
              },
            }}>
              Get Started
            </Button>
            <Button component={RouterLink} to="/technology" variant="outlined" sx={{
              borderColor: '#d4af37', 
              color: '#d4af37', 
              fontWeight: 700, 
              fontSize: '0.85rem', 
              letterSpacing: '2px', 
              textTransform: 'uppercase', 
              padding: '18px 40px', 
              borderRadius: '4px',
              '&:hover': { borderColor: '#ffffff', color: '#ffffff', backgroundColor: 'rgba(212, 175, 55, 0.1)' },
            }}>
              View Technology
            </Button>
          </Stack>
        </Container>
      </Box>
      {/* ============ END HERO ============ */}

      {/* ============ MAIN CONTENT ============ */}
      <Container maxWidth="xl" sx={{ py: { xs: 8, md: 12 } }}>
        <Grid container spacing={{ xs: 8, md: 10 }} alignItems="center">
          
          {/* Left Side: Editorial Text */}
          <Grid item xs={12} md={5}>
            <Box sx={{ maxWidth: '480px', mx: { xs: 'auto', md: 0 } }}>
              <Typography variant="overline" sx={{ color: '#d4af37', letterSpacing: '4px', fontWeight: 600, mb: 2, display: 'block' }}>
                The Future of Stable Assets
              </Typography>
              
              <Typography variant="h2" component="h2" sx={{ 
                fontFamily: "'Playfair Display', serif", color: '#ffffff', 
                fontSize: { xs: '2.5rem', md: '3.5rem' }, lineHeight: 1.2, mb: 4,
                textShadow: '0 0 40px rgba(212, 175, 55, 0.15)'
              }}>
                Digital Value,<br />Tangible Trust
              </Typography>
              
              <Typography variant="body1" sx={{ color: '#a1a1a1', fontSize: '1.05rem', lineHeight: 1.9, mb: 5 }}>
                Diamond Dollar is a next-generation stablecoin 
                <strong style={{ color: '#d4af37', fontWeight: 500 }}> Fully transparent and built for the future of finance.</strong>
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 5 }}>
                <Divider sx={{ borderColor: '#d4af37', width: '60px', mr: 2, borderBottomWidth: 2 }} />
                <Typography variant="caption" sx={{ color: '#d4af37', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 600 }}>
                  The New Standard
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 6 }}>
                <Box>
                 
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontFamily: "'Playfair Display', serif", color: '#d4af37', fontSize: '2.2rem', lineHeight: 1, textShadow: '0 0 20px rgba(212, 175, 55, 0.3)' }}>
                    On-Chain
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#a1a1a1', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    Audited
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Right Side: Cards - 3 cards now */}
          <Grid item xs={12} md={7}>
            <Grid container spacing={3}>
              {features.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: 'flex' }}>
                  <Paper elevation={0} sx={{
                    p: 3.5,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'linear-gradient(135deg, #141414 0%, #0a0a0a 100%)',
                    border: '1px solid rgba(212, 175, 55, 0.15)',
                    borderRadius: '6px',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.4s ease',
                    height: '100%',
                    minHeight: '280px',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '50%',
                      background: 'linear-gradient(to bottom, rgba(255,255,255,0.05), transparent)',
                      pointerEvents: 'none',
                    },
                    '&:hover': { 
                      borderColor: '#d4af37',
                      transform: 'translateY(-5px)', 
                      boxShadow: '0 20px 40px rgba(0,0,0,0.8), 0 0 20px rgba(212, 175, 55, 0.2)',
                      '& .icon-box': {
                        borderColor: '#d4af37',
                        color: '#d4af37',
                        backgroundColor: 'rgba(212, 175, 55, 0.1)',
                      }
                    },
                  }}>
                    <Box 
                      className="icon-box"
                      sx={{ 
                        color: '#d4af37', 
                        height: '50px', 
                        width: '50px',
                        mb: 2, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        borderRadius: '4px',
                        border: '1px solid rgba(212, 175, 55, 0.3)',
                        backgroundColor: 'rgba(212, 175, 55, 0.05)',
                        transition: 'all 0.4s ease',
                        '& svg': { fontSize: '2rem' } 
                      }}
                    >
                      {item.icon}
                    </Box>

                    <Typography variant="h6" sx={{ 
                      fontFamily: "'Playfair Display', serif", color: '#ffffff', 
                      mb: 1.5, fontSize: '1.1rem',
                      minHeight: '28px'
                    }}>
                      {item.title}
                    </Typography>

                    <Typography variant="body2" sx={{ 
                      color: '#a1a1a1', lineHeight: 1.7, fontSize: '0.85rem', 
                      mb: 2, 
                      flex: 1
                    }}>
                      {item.desc}
                    </Typography>

                    <Typography 
                      component="div" 
                      sx={{ 
                        display: 'flex', alignItems: 'center', gap: 0.5, 
                        color: '#d4af37', fontSize: '0.75rem', fontWeight: 600, 
                        textTransform: 'uppercase', letterSpacing: '1px', cursor: 'pointer',
                        transition: 'color 0.3s ease',
                        mt: 'auto',
                        '&:hover': { color: '#ffffff' },
                        '&:hover .MuiSvgIcon-root': { transform: 'translateX(4px)' }
                      }}
                    >
                      {item.link}
                      <ArrowForwardIcon sx={{ fontSize: '1rem', transition: 'transform 0.3s ease' }} />
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
}

export default About;